/**
 * Installs page (099) — the page's rules, no DOM. Ported from the staff console's
 * installs-model.js via INSTALLS_PAGE_PORTAL_SPEC.md §5. Everything here runs over the rows
 * already in the browser: filters, the story, sort and the URL never re-query.
 */

export const WINDOW_HOURS = 48;

/** Every build the export knows (platform AND app id — see the server's BUILDS). */
export const BUILDS = [
  { key: 'android-production', label: 'Android · Production' },
  { key: 'ios-production', label: 'iOS · Production' },
  { key: 'android-internal', label: 'Android · Internal' },
  { key: 'ios-internal', label: 'iOS · Internal' },
  { key: 'android-dev', label: 'Android · Dev' },
  { key: 'ios-dev', label: 'iOS · Dev' },
];
/** The builds the portal syncs — the only ones the Build select offers. */
export const SYNCED_BUILDS = BUILDS.filter((b) => b.key === 'android-production' || b.key === 'ios-production');
export const buildLabel = (key) => BUILDS.find((b) => b.key === key)?.label ?? '';

export const ANSWERS = [
  { key: 'opened', label: 'Opened app', event: 'session_start' },
  { key: 'install', label: 'Install event', event: 'pauser_app_install' },
  { key: 'signInStart', label: 'Sign-in started', event: 'custom_login_start' },
  { key: 'signedIn', label: 'Signed in', event: 'a sign-in' },
];
const ANSWER_KEYS = ANSWERS.map((a) => a.key);

const fu = (row) => row?.followUps ?? {};
const has = (v) => v !== null && v !== undefined;

/** Any of the four answers arrived — broader than `opened` on purpose. */
export const reached = (row) => ANSWER_KEYS.some((k) => has(fu(row)[k]));

/** Pending AND still missing an answer. What "pending" means everywhere on the page. */
export const stillOpen = (row) => !!row?.pending && ANSWER_KEYS.some((k) => !has(fu(row)[k]));

export const SIGN_IN_KINDS = {
  new_account: { tag: 'new', title: 'A new account — custom_sign_up_success', filter: 'New accounts', event: 'custom_sign_up_success' },
  existing_account: { tag: 'existing', title: 'An existing account — custom_login_success', filter: 'Existing accounts', event: 'custom_login_success' },
  identity_only: {
    tag: 'id only',
    title: 'A user_id appeared, but neither success event did — signed in, with the success event missing',
    filter: 'Id only',
    event: 'first event with a user_id',
  },
};
export const KIND_ORDER = ['new_account', 'existing_account', 'identity_only'];

/* ── Story ── */

export const STEPS = [
  { key: 'first-open', label: 'First opened', story: 'Every install', test: () => true },
  // "Install the app" (client, 2026-09-28): installs that sent pauser_app_install within 48 h —
  // the Install event column's ticks. The key stays `opened` so shared links keep working.
  // The sign-in steps still count EVERY install, and many sign in without ever sending the install
  // event, so they can be larger than this step: from here on the strip is not a strict funnel.
  { key: 'opened', label: 'Install the app', story: 'Sent the install event', test: (r) => has(fu(r).install) },
  // Only a real custom_login_start counts — the Sign-in started column's ticks. A sign-in with no
  // start (a user id restored ~2 s after first open on a reinstall, "id only") is not a start, so
  // "Signed in" can be larger than this step.
  { key: 'sign-in-started', label: 'Started sign-in', story: 'Started a sign-in', test: (r) => has(fu(r).signInStart) },
  { key: 'signed-in', label: 'Signed in', story: 'Signed in', test: (r) => has(fu(r).signedIn) },
];

// Each drop is the rows of its step that did not reach the next one. Only first open → install is
// nested, so only there is a step minus its drop the next step (see the steps above).
export const DROPS = [
  { key: 'never-opened', label: 'No install event', story: 'Never sent the install event' },
  { key: 'no-sign-in', label: 'Installed, no sign-in', story: 'Sent the install event, never started a sign-in' },
  { key: 'sign-in-dropped', label: 'Sign-in not finished', story: 'Started a sign-in, did not finish' },
].map((d, i) => ({ ...d, after: STEPS[i].key, test: (r) => STEPS[i].test(r) && !STEPS[i + 1].test(r) }));

const FOCUS = new Map([...STEPS, ...DROPS].map((x) => [x.key, x]));
export const focusItem = (key) => FOCUS.get(key) ?? FOCUS.get('first-open');

/* ── Flags ── */

export const paramValue = (row, key) => (row?.params ?? []).find((p) => p.key === key)?.value ?? null;
const prevCount = (row) => {
  const n = Number(paramValue(row, 'previous_first_open_count'));
  return Number.isFinite(n) ? n : 0;
};

export const FLAGS = [
  {
    key: 'non-store', label: 'Not a store install', tone: 'warn',
    test: (r) => !!r.installSource && !['play_store', 'app_store'].includes(r.installSource),
    chip: (r) => String(r.installSource).replace(/_/g, ' '),
  },
  { key: 'reinstall', label: 'Opened fresh before', tone: 'info', test: (r) => prevCount(r) > 0, chip: (r) => `reinstall ×${prevCount(r)}` },
  { key: 'no-session', label: 'No session', tone: 'muted', test: (r) => !has(r.sessionId), chip: () => 'no session' },
  // Error and cancel only on rows that never signed in — a retry that worked is not a fault.
  { key: 'sign-in-error', label: 'Sign-in error', tone: 'danger', test: (r) => !has(fu(r).signedIn) && has(r.signInErrorMillis), chip: () => 'sign-in error' },
  { key: 'sign-in-cancelled', label: 'Sign-in cancelled', tone: 'muted', test: (r) => !has(fu(r).signedIn) && has(r.signInCancelMillis), chip: () => 'sign-in cancelled' },
];
const FLAG_KEYS = FLAGS.map((f) => f.key);
export const flagsFor = (row) => FLAGS.filter((f) => f.test(row));

/* ── Filters ── */

export const DEFAULT_FILTERS = Object.freeze({ focus: 'first-open', install: 'any', status: 'any', account: 'any', flags: [], text: '' });

export const FILTER_GROUPS = [
  {
    key: 'install', label: 'Install event',
    options: [
      { value: 'any', label: 'Any' },
      { value: 'missing', label: 'Missing', chip: 'No install event', test: (r) => !has(fu(r).install) },
      { value: 'present', label: 'Present', chip: 'Has install event', test: (r) => has(fu(r).install) },
    ],
  },
  {
    key: 'status', label: 'Answers',
    options: [
      { value: 'any', label: 'Any' },
      { value: 'pending', label: 'Still pending', chip: 'Still pending', test: stillOpen },
      { value: 'final', label: 'Final', chip: 'Final answers only', test: (r) => !stillOpen(r) },
    ],
  },
  {
    key: 'account', label: 'Account',
    options: [
      { value: 'any', label: 'Any' },
      ...KIND_ORDER.map((k) => ({ value: k, label: SIGN_IN_KINDS[k].filter, chip: SIGN_IN_KINDS[k].filter, test: (r) => r.signInKind === k })),
      { value: 'none', label: 'Not signed in', chip: 'Not signed in', test: (r) => !has(fu(r).signedIn) },
    ],
  },
];
const GROUP = new Map(FILTER_GROUPS.map((g) => [g.key, g]));
const optionOf = (group, value) => GROUP.get(group).options.find((o) => o.value === value);

/** Unknown values become defaults; flags keep known order, deduped. Never throws. */
export function normaliseFilters(input) {
  const f = input && typeof input === 'object' ? input : {};
  const out = { ...DEFAULT_FILTERS, flags: [] };
  if (typeof f.focus === 'string' && FOCUS.has(f.focus)) out.focus = f.focus;
  for (const g of FILTER_GROUPS) {
    if (typeof f[g.key] === 'string' && optionOf(g.key, f[g.key])) out[g.key] = f[g.key];
  }
  const picked = Array.isArray(f.flags) ? f.flags : [];
  out.flags = FLAG_KEYS.filter((k) => picked.includes(k));
  out.text = typeof f.text === 'string' ? f.text : '';
  return out;
}

export function haystack(row) {
  const kind = SIGN_IN_KINDS[row.signInKind]?.tag ?? '';
  return [
    row.userPseudoId, paramValue(row, 'device_id') ?? row.installDeviceId, row.signedInUserId,
    row.deviceModel, row.osVersion, row.country, row.appVersion, row.platform, row.installSource,
    kind, buildLabel(row.target),
  ].filter(has).join(' ').toLowerCase();
}

/** Does the row pass every filter except `except` (a filter key, or null for all)? */
export function passes(row, filters, except = null) {
  const f = filters;
  if (except !== 'focus' && !focusItem(f.focus).test(row)) return false;
  for (const g of FILTER_GROUPS) {
    if (except === g.key || f[g.key] === 'any') continue;
    if (!optionOf(g.key, f[g.key]).test(row)) return false;
  }
  if (except !== 'flags' && f.flags.length) {
    const mine = flagsFor(row).map((x) => x.key);
    if (!f.flags.some((k) => mine.includes(k))) return false;
  }
  if (except !== 'text') {
    const t = f.text.trim().toLowerCase();
    if (t && !haystack(row).includes(t)) return false;
  }
  return true;
}

export const visibleRows = (rows, filters) => rows.filter((r) => passes(r, filters));

/** The story strip: counted over rows passing every filter but focus. */
export function story(rows, filters) {
  const base = rows.filter((r) => passes(r, filters, 'focus'));
  const total = base.length;
  const steps = STEPS.map((s) => {
    const inStep = base.filter(s.test);
    return { ...s, count: inStep.length, share: total ? inStep.length / total : 0, pending: inStep.filter(stillOpen).length };
  });
  const drops = DROPS.map((d) => {
    const inDrop = base.filter(d.test);
    return { ...d, count: inDrop.length, pending: inDrop.filter(stillOpen).length };
  });
  const kinds = Object.fromEntries(KIND_ORDER.map((k) => [k, base.filter((r) => has(fu(r).signedIn) && r.signInKind === k).length]));
  return { total, steps, drops, kinds, pending: base.filter(stillOpen).length };
}

/** Counts beside each option in the Filters dialog: every OTHER filter applied, then this one. */
export function optionCounts(rows, filters) {
  const out = {};
  for (const g of FILTER_GROUPS) {
    const base = rows.filter((r) => passes(r, filters, g.key));
    out[g.key] = Object.fromEntries(g.options.map((o) => [o.value, o.test ? base.filter(o.test).length : base.length]));
  }
  const flagBase = rows.filter((r) => passes(r, filters, 'flags'));
  out.flags = Object.fromEntries(FLAGS.map((fl) => [fl.key, flagBase.filter(fl.test).length]));
  return out;
}

/** Filters owned by the dialog (not focus, not text) — the badge on the Filters button. */
export const dialogFilterCount = (f) =>
  FILTER_GROUPS.filter((g) => f[g.key] !== 'any').length + f.flags.length;

/** Chips for the Showing line, in the spec's order. `remove` is the patch that clears it. */
export function describe(filters) {
  const f = filters;
  const chips = [];
  if (f.focus !== DEFAULT_FILTERS.focus) chips.push({ key: 'focus', text: focusItem(f.focus).story, remove: { focus: 'first-open' } });
  for (const g of FILTER_GROUPS) {
    if (f[g.key] !== 'any') chips.push({ key: g.key, text: optionOf(g.key, f[g.key]).chip, remove: { [g.key]: 'any' } });
  }
  for (const k of f.flags) {
    chips.push({ key: `flag:${k}`, text: `Flag: ${FLAGS.find((x) => x.key === k).label}`, remove: { flags: f.flags.filter((x) => x !== k) } });
  }
  if (f.text.trim()) chips.push({ key: 'text', text: `Matches "${f.text.trim()}"`, remove: { text: '' } });
  return chips;
}

/* ── Sort ── */

export const SORT_FIELDS = [
  { key: 'first-open', label: 'First open' },
  { key: 'opened', label: 'Opened app' },
  { key: 'install', label: 'Install event' },
  { key: 'signInStart', label: 'Sign-in started' },
  { key: 'signedIn', label: 'Signed in' },
];
export const DEFAULT_SORT = Object.freeze({ field: 'first-open', dir: 'desc' });

const sortValue = (row, field) => {
  const v = field === 'first-open' ? row.timestampMillis : fu(row)[field];
  return has(v) ? v : -Infinity; // missing = older than any real date
};

/** A new array; ties fall back to anchor time in the same direction, then arrival order. */
export function sortRows(rows, field = 'first-open', dir = 'desc') {
  const f = SORT_FIELDS.some((s) => s.key === field) ? field : 'first-open';
  const sign = dir === 'asc' ? 1 : -1;
  const cmp = (a, b) => (a === b ? 0 : a < b ? -1 : 1);
  return rows
    .map((row, i) => ({ row, i }))
    .sort((a, b) => sign * cmp(sortValue(a.row, f), sortValue(b.row, f))
      || sign * cmp(a.row.timestampMillis, b.row.timestampMillis)
      || a.i - b.i)
    .map((x) => x.row);
}

/* ── URL hash ── */

const LEGACY_VIEW = {
  all: {},
  'never-opened': { focus: 'never-opened' },
  'no-sign-in': { focus: 'no-sign-in' },
  'sign-in-dropped': { focus: 'sign-in-dropped' },
  'signed-in': { focus: 'signed-in' },
  'has-opened': { focus: 'opened' },
  'has-install': { install: 'present' },
  'has-signInStart': { focus: 'sign-in-started' },
  'has-signedIn': { focus: 'signed-in' },
  'no-install': { install: 'missing' },
  pending: { status: 'pending' },
};

/**
 * Read `#from=…&to=…&build=…&v=…&device=…&rows=…&focus=…&…&sort=…&dir=…`.
 * `search` is null when there is no `from` (the page then opens the Search dialog).
 * A bad or old hash never throws; legacy `view=` still loads, and a valid new key wins over it.
 */
export function parseHash(hash) {
  let p;
  try {
    p = new URLSearchParams(String(hash ?? '').replace(/^#/, ''));
  } catch {
    p = new URLSearchParams();
  }
  const get = (k) => p.get(k) ?? '';
  const legacy = LEGACY_VIEW[get('view')] ?? {};
  const raw = { ...legacy };
  for (const k of ['focus', 'install', 'status', 'account']) {
    const v = get(k);
    if (v && normaliseFilters({ [k]: v })[k] === v) raw[k] = v;
  }
  raw.flags = get('flags') ? get('flags').split(',') : [];
  raw.text = get('q');
  const filters = normaliseFilters(raw);

  const dir = get('dir') === 'asc' ? 'asc' : 'desc';
  const field = SORT_FIELDS.some((s) => s.key === get('sort')) ? get('sort') : DEFAULT_SORT.field;

  const from = get('from');
  const search = /^\d{4}-\d{2}-\d{2}$/.test(from)
    ? {
      from,
      to: /^\d{4}-\d{2}-\d{2}$/.test(get('to')) ? get('to') : from,
      build: get('build'),
      versions: get('v'),
      device: get('device'),
      rows: /^\d+$/.test(get('rows')) ? get('rows') : '',
    }
    : null;
  return { search, filters, sort: { field, dir } };
}

/** Only values that differ from the default are written. */
export function serializeHash({ search, filters, sort }) {
  const p = new URLSearchParams();
  if (search) {
    p.set('from', search.from);
    if (search.to && search.to !== search.from) p.set('to', search.to);
    if (search.build) p.set('build', search.build);
    if (search.versions) p.set('v', search.versions);
    if (search.device) p.set('device', search.device);
    if (search.rows) p.set('rows', String(search.rows));
  }
  const f = normaliseFilters(filters);
  for (const k of ['focus', 'install', 'status', 'account']) if (f[k] !== DEFAULT_FILTERS[k]) p.set(k, f[k]);
  if (f.flags.length) p.set('flags', f.flags.join(','));
  if (f.text) p.set('q', f.text);
  if (sort && sort.field !== DEFAULT_SORT.field) p.set('sort', sort.field);
  if (sort && sort.dir !== DEFAULT_SORT.dir) p.set('dir', sort.dir);
  return p.toString();
}

/* ── Search dialog helpers ── */

/** Versions newest first, numerically: 1.0.29 > 1.0.9. */
export function compareVersionsDesc(a, b) {
  const pa = String(a).split(/[^0-9]+/).filter(Boolean).map(Number);
  const pb = String(b).split(/[^0-9]+/).filter(Boolean).map(Number);
  for (let i = 0; i < Math.max(pa.length, pb.length); i += 1) {
    const d = (pb[i] ?? -1) - (pa[i] ?? -1);
    if (d) return d;
  }
  return String(b).localeCompare(String(a));
}

const DAY_MS = 86400000;
export const utcDay = (now, offset = 0) => new Date(Math.floor(now / DAY_MS) * DAY_MS + offset * DAY_MS).toISOString().slice(0, 10);

export const PRESETS = [
  { key: 'today', label: 'Today', range: [0, 0] },
  { key: 'yesterday', label: 'Yesterday', range: [-1, -1] },
  { key: 'three', label: '3 days', range: [-2, 0] },
];
export const presetDates = (preset, now) => ({ from: utcDay(now, preset.range[0]), to: utcDay(now, preset.range[1]) });

export const MAX_RANGE_DAYS = 31;

/**
 * Validation for the Search dialog; null when the search may run. `maxDays` is 31 for the synced
 * copy and 3 when the page asks BigQuery itself (112).
 */
export function validateSearch({ from, to }, maxDays = MAX_RANGE_DAYS) {
  if (!from || !to) return 'Pick both dates.';
  if (to < from) return 'The range ends before it starts.';
  const days = Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / DAY_MS) + 1;
  if (days > maxDays) return `That range is ${days} days; the most is ${maxDays}.`;
  return null;
}
